import { UserViewContext } from '../types';
import { routeMetadataRegistry } from '../metadata';
import matchRoutePattern from './matchRoutePattern';

type DeriveUserViewContextParams = {
    pathname: string;
    params: Record<string, string | undefined>;
    queryParams?: Record<string, string | undefined>;
};

/**
 * Interpolates a template string with parameter values.
 * Example: interpolate('User is editing {promptId}', { promptId: 'prompt_123' })
 * Result: 'User is editing prompt_123'
 */
const interpolate = (template: string, params: Record<string, string>): string => {
    return template.replace(/\{(\w+)\}/g, (_, paramName) => {
        return params[paramName] || `{${paramName}}`;
    });
};

/**
 * Derives semantic user view context from the current URL.
 * Pure function - no side effects, easy to test.
 *
 * Matches the pathname against registered route patterns to find metadata,
 * then combines with URL params to produce a rich context object.
 */
const deriveUserViewContext = ({
    pathname,
    params,
    queryParams = {},
}: DeriveUserViewContextParams): UserViewContext => {
    // Try to match against all registered routes (most specific first)
    for (const metadata of routeMetadataRegistry) {
        const matchResult = matchRoutePattern({ pathname, pattern: metadata.pattern });

        if (matchResult.matched) {
            const allParams = { ...queryParams, ...matchResult.params, ...params };

            // Build the ids object from matched params
            const ids: UserViewContext['ids'] = {
                canvasId: allParams.canvasId,
                workflowId: allParams.workflowId,
                versionId: allParams.versionId,
                playbookId: allParams.playbookId,
                stepId: allParams.stepId,
                promptId: allParams.promptId,
                skillId: allParams.skillId,
                metricId: allParams.metricId,
                dashboardId: allParams.dashboardId,
                fileId: allParams.fileId,
                templateId: allParams.templateId,
                bucketName: allParams.bucketName,
                threadId: allParams.threadId,
                runId: allParams.runId,
                eventId: allParams.eventId,
                mode: allParams.mode,
                systemAgentId: allParams.systemAgentId,
                agentType: allParams.agentType || allParams.systemAgentId,
                namespace: allParams.namespace,
                searchQuery: allParams.search,
                statusFilter: allParams.status,
                daysBack: allParams.daysBack,
                organizationId: allParams.organizationId || allParams.agentId,
            };

            // Remove undefined values
            Object.keys(ids).forEach(key => {
                if (ids[key as keyof typeof ids] === undefined) {
                    delete ids[key as keyof typeof ids];
                }
            });

            // Filter out undefined values for interpolation
            const definedParams: Record<string, string> = {};
            Object.entries(allParams).forEach(([key, value]) => {
                if (value !== undefined) {
                    definedParams[key] = value;
                }
            });

            return {
                agentId: allParams.agentId || '',
                section: metadata.section,
                view: metadata.view,
                ids,
                description: interpolate(metadata.descriptionTemplate, definedParams),
                capabilities: metadata.capabilities,
                hints: metadata.hints,
            };
        }
    }

    // No match found - return default context
    return {
        agentId: params.agentId || '',
        section: 'workflows',
        view: 'unknown',
        ids: {},
        description: `User is viewing ${pathname}`,
        capabilities: [],
        hints: [],
    };
};

export default deriveUserViewContext;
